import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import periodoService from '../../services/periodo.service';
import SePode from '../../components/SePode';   // r90

// ════════════════════════════════════════════════════════════════════════════
// PeriodoCorrecoesModal — gerencia colaboradores em correção
// ────────────────────────────────────────────────────────────────────────────
// 2 modos:
//   - periodo.status === 'F' → mostra checkbox; ao confirmar, INICIA correções
//   - periodo.status === 'C' → mostra checkboxes com os já selecionados,
//                              permite adicionar/remover
// ════════════════════════════════════════════════════════════════════════════

const COR = {
  azul:    '#003399', azulBg:  '#eff6ff',
  borda:   '#e8eaed', texto:   '#1a1a2e', muted: '#888',
  branco:  '#fff',    cinzaBg: '#fafafa',
  verde:   '#10b981',
  vermelho:'#dc2626',
  amarelo: '#d97706', amlBg:   '#fffbeb',
};

function PeriodoCorrecoesModal({ periodo, onFechar }) {
  const { usuario } = useAuth();
  const [colabs, setColabs] = useState([]);
  const [selecionados, setSelecionados] = useState(new Set());
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  const carregar = useCallback(async () => {
    try {
      setCarregando(true);
      const lista = await periodoService.colaboradoresComLancamento(periodo.id);
      setColabs(lista);
      // Pré-seleciona quem já está em correção
      const sel = new Set(lista.filter(c => c.ja_em_correcao).map(c => c.id));
      setSelecionados(sel);
    } catch (err) {
      setErro('Erro ao carregar colaboradores: ' + err.message);
    } finally {
      setCarregando(false);
    }
  }, [periodo.id]);

  useEffect(() => { carregar(); }, [carregar]);

  const toggle = (id) => {
    setSelecionados(prev => {
      const novo = new Set(prev);
      if (novo.has(id)) novo.delete(id);
      else novo.add(id);
      return novo;
    });
  };

  const salvar = async () => {
    setErro('');
    if (selecionados.size === 0 && periodo.status === 'F') {
      setErro('Selecione pelo menos 1 colaborador.');
      return;
    }
    try {
      setSalvando(true);

      // 1) Identifica diferenças entre o estado inicial e o atual
      const original = new Set(colabs.filter(c => c.ja_em_correcao).map(c => c.id));
      const adicionar = [...selecionados].filter(id => !original.has(id));
      const remover   = [...original].filter(id => !selecionados.has(id));

      // 2) Remove primeiro (cada DELETE devolve lançamentos de C pra F)
      for (const colabId of remover) {
        await periodoService.removerColaboradorCorrecao(periodo.id, colabId);
      }

      // 3) Adiciona (POST único pra todos os novos)
      if (adicionar.length > 0) {
        await periodoService.iniciarCorrecoes(periodo.id, usuario.id, adicionar);
      }

      onFechar(true);
    } catch (err) {
      setErro(err.response?.data?.mensagem || err.message);
      setSalvando(false);
    }
  };

  const mmaaaa = `${String(periodo.mes).padStart(2, '0')}/${periodo.ano}`;
  const totalSel = selecionados.size;

  return (
    <div style={{
      position: 'fixed', inset: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '20px',
    }} onClick={(e) => e.target === e.currentTarget && onFechar(false)}>
      <div style={{
        backgroundColor: COR.branco, borderRadius: '8px',
        width: '560px', maxWidth: '100%', maxHeight: '85vh',
        display: 'flex', flexDirection: 'column',
        fontFamily: "'Manrope', sans-serif",
      }}>
        <div style={{
          padding: '14px 18px', borderBottom: `1px solid ${COR.borda}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <h2 style={{ margin: 0, fontSize: '16px', fontWeight: '700' }}>
            {periodo.status === 'C' ? 'Gerenciar Correções' : 'Iniciar Correções'} — {mmaaaa}
          </h2>
          <button onClick={() => onFechar(false)}
            style={{
              width: '28px', height: '28px', border: 'none',
              backgroundColor: 'transparent', fontSize: '20px',
              color: COR.muted, cursor: 'pointer',
            }}>×</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px' }}>
          <p style={{ fontSize: '13px', color: COR.texto, marginTop: 0 }}>
            Selecione os colaboradores que poderão alterar lançamentos neste período.
            Os lançamentos dos selecionados serão marcados como <strong>"Correção"</strong>.
            Lançamentos de colaboradores não selecionados permanecem como <strong>"Fechada"</strong>.
          </p>

          {erro && (
            <div style={{
              padding: '8px 12px', marginBottom: '12px',
              backgroundColor: '#fff0f0', color: COR.vermelho,
              border: `1px solid ${COR.vermelho}33`, borderRadius: '5px',
              fontSize: '12px', fontWeight: '600',
            }}>{erro}</div>
          )}

          {carregando ? (
            <div style={{ textAlign: 'center', color: COR.muted, padding: '20px' }}>
              Carregando colaboradores...
            </div>
          ) : colabs.length === 0 ? (
            <div style={{ textAlign: 'center', color: COR.muted, fontStyle: 'italic', padding: '20px' }}>
              Nenhum colaborador com lançamentos neste período.
            </div>
          ) : (
            <div style={{
              border: `1px solid ${COR.borda}`, borderRadius: '6px',
              maxHeight: '380px', overflowY: 'auto',
            }}>
              {colabs.map(c => {
                const checked = selecionados.has(c.id);
                return (
                  <label key={c.id} style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '10px 14px', cursor: 'pointer',
                    backgroundColor: checked ? COR.amlBg : COR.branco,
                    borderBottom: `1px solid ${COR.borda}`,
                  }}>
                    <input type="checkbox" checked={checked} onChange={() => toggle(c.id)} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: COR.texto }}>
                        {c.nome_amigavel || c.nome}
                      </div>
                      <div style={{ fontSize: '11px', color: COR.muted, marginTop: '2px' }}>
                        {c.qtd_lancamentos} lançamento{c.qtd_lancamentos !== 1 ? 's' : ''} no período
                        {c.ja_em_correcao && (
                          <span style={{
                            marginLeft: '8px', padding: '1px 6px',
                            backgroundColor: COR.amarelo + '22', color: COR.amarelo,
                            borderRadius: '8px', fontWeight: '600',
                          }}>já em correção</span>
                        )}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <div style={{
          padding: '12px 18px', borderTop: `1px solid ${COR.borda}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span style={{ fontSize: '12px', color: COR.muted }}>
            {totalSel} colaborador{totalSel !== 1 ? 'es' : ''} selecionado{totalSel !== 1 ? 's' : ''}
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => onFechar(false)}
              style={{
                padding: '8px 16px', fontSize: '12px', fontWeight: '600',
                border: `1px solid ${COR.borda}`, borderRadius: '5px',
                backgroundColor: COR.branco, color: COR.muted, cursor: 'pointer',
              }}>Cancelar</button>
            <SePode rotina="periodos" acao="abrir-correcoes">
            <button onClick={salvar} disabled={salvando}
              style={{
                padding: '8px 20px', fontSize: '12px', fontWeight: '700',
                border: 'none', borderRadius: '5px',
                backgroundColor: COR.amarelo, color: COR.branco,
                cursor: salvando ? 'default' : 'pointer',
                opacity: salvando ? 0.6 : 1,
              }}>{salvando ? 'Salvando...' : 'Confirmar'}</button>
            </SePode>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PeriodoCorrecoesModal;
